import React, {useEffect, useState} from "react";
import {Location, Session, User} from "../../types/Types";
import {Card} from "react-bootstrap";
import {httpsCallable} from "@firebase/functions";
import {db, functions} from "../../../firebase/FirebaseProvider";
import {collection, getDocs, query, where} from "@firebase/firestore";
import {formatMetres, getTime} from "../../util/Util";
import {Link} from "react-router-dom";

interface Props {
    location: Location,
    currentUser: User,
    variant?: "default" | "v2Header"
}

function LocationCheckIn(props: Props) {
    const CHECK_IN_COOLDOWN_MS = 60 * 60 * 1000;
    const MINIMUM_CHECK_OUT_MS = 15 * 60 * 1000;

    const [checkInStatus, setCheckInStatus] = useState<number | undefined>(-1);
    const [hasCheckedIn, setHasCheckedIn] = useState<boolean>(false);
    const [session, setSession] = useState<Session>();
    const [feedback, setFeedback] = useState<string>();
    const [cooldownEndsAt, setCooldownEndsAt] = useState<Date | undefined>(undefined);
    const [checkOutAvailableAtOverride, setCheckOutAvailableAtOverride] = useState<Date | undefined>(undefined);
    const [now, setNow] = useState<Date>(new Date());

    useEffect(() => {
        (async () => {
            await fetchSessionStatus();
        })();
    }, [props.currentUser, props.location.id]);

    useEffect(() => {
        if (!cooldownEndsAt && !session) {
            return;
        }

        const intervalId = window.setInterval(() => {
            const currentTime = new Date();
            setNow(currentTime);
            if (cooldownEndsAt && cooldownEndsAt.getTime() <= currentTime.getTime()) {
                setCooldownEndsAt(undefined);
                setFeedback(undefined);
            }
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, [cooldownEndsAt, session]);

    const getDateFromTimestamp = (timestamp: any): Date | undefined => {
        if (!timestamp) {
            return undefined;
        }
        if (timestamp.toDate) {
            return timestamp.toDate();
        }
        if (timestamp.seconds) {
            return new Date(timestamp.seconds * 1000);
        }

        const date = new Date(timestamp);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }

    const getCooldownFeedback = (endsAt: Date | undefined = cooldownEndsAt) => {
        if (!endsAt) {
            return undefined;
        }

        const remainingSeconds = Math.max(0, Math.ceil((endsAt.getTime() - now.getTime()) / 1000));
        if (remainingSeconds <= 0) {
            return undefined;
        }

        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        if (minutes === 0) {
            return `Check-in available in ${seconds} seconds.`;
        }

        return `Check-in available in ${minutes} minutes.`;
    }

    const formatCountdown = (remainingSeconds: number): string => {
        const minutes = Math.floor(remainingSeconds / 60).toString().padStart(2, "0");
        const seconds = (remainingSeconds % 60).toString().padStart(2, "0");
        return `${minutes}:${seconds}`;
    }

    const getCheckOutAvailableAt = (): Date | undefined => {
        if (checkOutAvailableAtOverride) {
            return checkOutAvailableAtOverride;
        }

        if (!session) {
            return undefined;
        }

        const checkInTime = getDateFromTimestamp(session.checkInTime);
        if (!checkInTime) {
            return undefined;
        }

        return new Date(checkInTime.getTime() + MINIMUM_CHECK_OUT_MS);
    }

    const getCheckOutRemainingSeconds = (): number => {
        const checkOutAvailableAt = getCheckOutAvailableAt();
        if (!checkOutAvailableAt) {
            return 0;
        }

        return Math.max(0, Math.ceil((checkOutAvailableAt.getTime() - now.getTime()) / 1000));
    }

    const fetchCooldownStatus = async() => {
        const cutoff = new Date(Date.now() - CHECK_IN_COOLDOWN_MS);
        const cooldownCollection = query(collection(db, 'sessions'),
            where('userId', '==', props.currentUser.id),
            where('locationId', '==', props.location.id),
            where('checkInTime', '>', cutoff)
        );
        const cooldownSnapshot = await getDocs(cooldownCollection);
        let latestCooldownEndsAt: Date | undefined;
        cooldownSnapshot.forEach((doc) => {
            const sessionItem = doc.data() as Session;
            const checkInTime = getDateFromTimestamp(sessionItem.checkInTime);
            if (!checkInTime) {
                return;
            }

            const sessionCooldownEndsAt = new Date(checkInTime.getTime() + CHECK_IN_COOLDOWN_MS);
            if (
                sessionCooldownEndsAt.getTime() > Date.now() &&
                (!latestCooldownEndsAt || sessionCooldownEndsAt.getTime() > latestCooldownEndsAt.getTime())
            ) {
                latestCooldownEndsAt = sessionCooldownEndsAt;
            }
        });

        setCooldownEndsAt(latestCooldownEndsAt);
        setFeedback(latestCooldownEndsAt ? getCooldownFeedback(latestCooldownEndsAt) : undefined);
    }

    const fetchSessionStatus = async() => {
        const sessionCollection = query(collection(db, 'sessions'),
            where('userId', '==', props.currentUser.id),
            where('checkOutTime', '>', new Date())
        );
        const newsOnceCollectionSnapshot = await getDocs(sessionCollection);
        let count = 0;
        newsOnceCollectionSnapshot.forEach((doc) => {
            count++;
            let sessionItem = doc.data() as Session;
            setCheckInStatus(1);
            setHasCheckedIn(true);
            setSession(sessionItem);
            setCooldownEndsAt(undefined);
            setCheckOutAvailableAtOverride(undefined);
            return;
        });
        if(count === 0) {
            setCheckInStatus(0);
            setHasCheckedIn(false);
            setSession(undefined);
            setCheckOutAvailableAtOverride(undefined);
            await fetchCooldownStatus();
        }
    }

    const applyCooldown = (cooldownEndsAtMillis: number) => {
        const cooldownEndDate = new Date(cooldownEndsAtMillis);
        setCooldownEndsAt(cooldownEndDate);
        setFeedback(getCooldownFeedback(cooldownEndDate));
        setHasCheckedIn(false);
        setCheckInStatus(0);
    }

    const handleCheckIn = () => {
        if (cooldownEndsAt && cooldownEndsAt.getTime() > Date.now()) {
            setFeedback(getCooldownFeedback());
            return;
        }

        setHasCheckedIn(true);
        navigator.geolocation.getCurrentPosition(
            async currentLocation => {
                try {
                    var checkIn = httpsCallable(functions, 'checkIn');
                    await checkIn({sessionRequest: {
                            locationId: props.location.id,
                            locationName: props.location.name,
                            lat: currentLocation.coords.latitude,
                            lng: currentLocation.coords.longitude
                        }}).then(r => {
                            const result = r.data as any;
                            if(result && result.code === "COOLDOWN" && result.cooldownEndsAt) {
                                applyCooldown(result.cooldownEndsAt);
                            } else if(result && result === -2) {
                                setHasCheckedIn(false);
                                setCheckInStatus(0);
                                setFeedback("This pub hasn't opened yet.");
                            } else if(result && result !== 1) {
                                setHasCheckedIn(false);
                                setCheckInStatus(0);
                                setFeedback("Move " + formatMetres(result as number) + " closer.");
                            } else {
                                setCooldownEndsAt(undefined);
                                setFeedback(undefined);
                                fetchSessionStatus();
                            }
                        });
                } catch (err) {
                    console.error(err);
                    setCheckInStatus(0);
                    setHasCheckedIn(false);
                }
            },
            err => {
                alert(
                    "Unable to get your location. Make sure you've enabled GPS! " +
                    err.message
                );
            },
            {
                maximumAge: 1000,
                enableHighAccuracy: true
            }
        );
    }

    const handleCheckOut = () => {
        if(!session) {
            return
        }

        const checkOutRemainingSeconds = getCheckOutRemainingSeconds();
        if (checkOutRemainingSeconds > 0) {
            return;
        }

        setHasCheckedIn(false);
        navigator.geolocation.getCurrentPosition(
            async currentLocation => {
                try {
                    var checkOut = httpsCallable(functions, 'checkOut');
                    await checkOut({sessionRequest: {
                            locationId: session.locationId,
                            locationName: session.locationName,
                            lat: currentLocation.coords.latitude,
                            lng: currentLocation.coords.longitude
                        }}).then(r => {
                        const result = r.data as any;
                        if(result && result.code === "CHECK_OUT_MINIMUM_TIME") {
                            if (result.checkOutAvailableAt) {
                                setCheckOutAvailableAtOverride(new Date(result.checkOutAvailableAt));
                            }
                            setHasCheckedIn(true);
                            setCheckInStatus(1);
                        } else if(result && result != 1) {
                            setHasCheckedIn(true);
                            setCheckInStatus(1);
                            setFeedback("Move " + formatMetres(result as number) + " closer to check out.");
                        } else {
                            const checkInTime = getDateFromTimestamp(session.checkInTime);
                            if (checkInTime) {
                                applyCooldown(checkInTime.getTime() + CHECK_IN_COOLDOWN_MS);
                            }
                            fetchSessionStatus();
                        }
                    });
                } catch (err) {
                    console.error(err);
                    setCheckInStatus(1);
                }
            },
            err => {
                alert(
                    "Unable to get your location. Make sure you've enabled GPS! " +
                    err.message
                );
            },
            {
                maximumAge: 1000,
                enableHighAccuracy: true
            }
        );
    }

    const pubIsClosed = props.location.pubIsClosed || props.location.pubIsTemporaryClosed;
    
    // Check if pub hasn't opened yet
    const isNotYetOpen = () => {
        if (!props.location.openingDate) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const openingDate = new Date(props.location.openingDate);
        openingDate.setHours(0, 0, 0, 0);
        return openingDate > today;
    };
    
    // Format opening date
    const formatOpeningDate = () => {
        if (!props.location.openingDate) return '';
        const date = new Date(props.location.openingDate);
        const day = date.getDate();
        const suffix = (day: number) => {
            if (day > 3 && day < 21) return 'th';
            switch (day % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        };
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        return `${day}${suffix(day)} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
    };
    
    const pubNotYetOpen = isNotYetOpen();
    const pubIsClosedOrNotOpen = pubIsClosed || pubNotYetOpen;
    const cooldownFeedback = getCooldownFeedback();
    const isCoolingDown = !!cooldownFeedback;
    const checkOutRemainingSeconds = getCheckOutRemainingSeconds();
    const checkOutLocked = checkOutRemainingSeconds > 0;
    const isV2Header = props.variant === "v2Header";
    const v2CheckInResult = cooldownFeedback || feedback || (pubIsClosed ? "Pub is closed." : undefined) || (pubNotYetOpen ? `Opening ${formatOpeningDate()}` : undefined);

    if (isV2Header) {
        return (
            <>
                {(checkInStatus == 0 || !session) && (
                    <>
                        {!pubIsClosedOrNotOpen && (
                            <button
                                className="location-v2-checkin-button"
                                disabled={pubIsClosedOrNotOpen || isCoolingDown}
                                onClick={() => handleCheckIn()}
                                type="button"
                            >
                                {!hasCheckedIn && <span>Check in</span>}
                                {hasCheckedIn && <span>Please wait...</span>}
                            </button>
                        )}
                    </>
                )}
                {checkInStatus == 1 && session && (
                    <button
                        className="location-v2-checkin-button location-v2-checkout-button"
                        disabled={checkOutLocked || !hasCheckedIn}
                        onClick={() => handleCheckOut()}
                        type="button"
                    >
                        {hasCheckedIn && checkOutLocked && <span>Checkout in {formatCountdown(checkOutRemainingSeconds)}</span>}
                        {hasCheckedIn && !checkOutLocked && <span>Check out</span>}
                        {!hasCheckedIn && <span>Please wait...</span>}
                    </button>
                )}
                {v2CheckInResult && (
                    <div className="location-v2-checkin-result">{v2CheckInResult}</div>
                )}
            </>
        );
    }

    return (
        <>
            {props.currentUser && <Card className={"mb-2"}>
                <Card.Body style={{padding: "0.5em"}}>
                    <>
                        {(checkInStatus == 0 || !session) && <div>
                            <div className={"d-inline-block"} style={{float: "left"}}>
                                <div className={"ml-1 d-inline-block"} style={{marginTop: "0.1em"}}>
                                    <span>{cooldownFeedback || feedback}</span>
                                </div>
                            </div>
                            <div className={"d-inline-block"} style={{float: "right"}}>
                                <div className={"d-inline-block"}>
                                    {pubIsClosed && <span>Pub is closed.</span>}
                                    {pubNotYetOpen && <span>Opening {formatOpeningDate()}</span>}
                                    {!pubIsClosedOrNotOpen && <>
                                        {!feedback && !cooldownFeedback && <span>Here now?</span>}
                                        <button disabled={pubIsClosedOrNotOpen || isCoolingDown} className={"btn btn-sm btn-primary ml-2"} onClick={() => handleCheckIn()}>
                                        {!hasCheckedIn && <span>Check in</span>}
                                        {hasCheckedIn && <span>Please wait...</span>}
                                        </button>
                                    </>
                                    }
                                </div>
                            </div>
                        </div>}
                        {checkInStatus == 1 && session && <div>
                            {feedback && <div className={"d-inline-block"} style={{float: "left"}}>
                                <div className={"ml-1 d-inline-block"} style={{marginTop: "0.1em"}}>
                                    <span>{feedback}</span>
                                </div>
                            </div>}
                            {!feedback &&
                                <>
                                    <div className={""}>
                                        <span>Checked into</span>
                                        <div className={"d-inline-block ml-1 mr-1"}>
                                            <Link to={`/location/${session.locationId}/comments/me`} className={"p-0"}>
                                                <div className={"location-tag"}>{session.locationName}</div>
                                            </Link>
                                        </div>
                                        {session.checkOutTime && <>
                                            <span>until </span>
                                            <div className={"d-inline-block"}>
                                                <span>{getTime(session.checkOutTime)}</span>
                                            </div>
                                        </>}
                                    </div>
                                    <div className={"mt-1"} style={{float: "right"}}>
                                        <div className={"d-inline-block"}>
                                            <button disabled={checkOutLocked || !hasCheckedIn} className={"btn btn-sm btn-primary ml-2 btn-danger"} onClick={() => handleCheckOut()}>
                                                {hasCheckedIn && checkOutLocked && <span>Checkout in {formatCountdown(checkOutRemainingSeconds)}</span>}
                                                {hasCheckedIn && !checkOutLocked && <span>Check out</span>}
                                                {!hasCheckedIn && <span>Please wait...</span>}
                                            </button>
                                        </div>
                                    </div>
                            </>
                            }
                        </div>}
                    </>
                </Card.Body>
            </Card>}
        </>
    );
}

export default LocationCheckIn;
